'use client';

import { NewsList } from '@/components/global';
import { usePrint } from '@/lib/hooks';
import { News as NewsT, PageData, PageProps } from '@/lib/types';
import { Body, Breadcrumb, CustomLink, DetailsLink, Section, ZoomImage } from '@/ui';
import { Content, NewsCard } from '@/widgets';
import { Anchor, Container, Flex, Image } from '@mantine/core';
import { useTranslations } from 'next-intl';
import NextImage from 'next/image';
import { useState } from 'react';

type Props = {
  data: PageProps & {
    activity: PageData;
    news: NewsT[];
  };
  blurredImg: string;
};

const News = ({ news }: { news: NewsT[] }) => {
  return (
    <div className="print:!hidden">
      <NewsList>
        {news?.map((news) => (
          <NewsCard key={news?.id} {...news} type="authority" url={`/news/view/${news.id}`} />
        ))}
      </NewsList>
    </div>
  );
};

export default function Activity({ data, blurredImg }: Props) {
  const t = useTranslations();
  const { ref, handlePrint } = usePrint();
  const [imgError, setImgError] = useState(false);

  const breadcrumbItems = [
    { title: data?.breadcrumb, href: `/general-menu/${data?.menu_id}` },
    { title: data?.activity?.title, isCurrent: true },
  ];

  return (
    <Container>
      <Section rightMenu={{ isActive: true, id: data?.menu_id, activeItem: { id: data?.menu_item_id } }}>
        <Content ref={ref}>
          <Content.Header details={{ handlePrint, ageLimit: data?.activity?.age_limit }}>
            <Breadcrumb items={breadcrumbItems} />
          </Content.Header>
          <Content.Title>{data?.activity?.title}</Content.Title>
          <Content.Body
            isShareable
            views={data?.activity?.views}
            updatedAt={data?.activity?.updated_date ? data?.activity?.updated_date : null}
          >
            <ZoomImage>
              {data?.activity?.image?.trim() && !imgError ? (
                <Image
                  component={NextImage}
                  src={data?.activity?.image}
                  maw="100%"
                  width={800}
                  height={400}
                  alt={data?.activity?.title}
                  placeholder={blurredImg?.trim() ? 'blur' : 'empty'}
                  blurDataURL={blurredImg}
                  onError={() => setImgError(true)}
                />
              ) : null}
              {data?.activity?.body?.trim() ? <Body content={data?.activity?.body} /> : null}
            </ZoomImage>

            {data?.activity?.has_question ? (
              <Anchor
                display="inline-block"
                component={CustomLink}
                href={`/faq?activity_id=${data?.activity?.id}`}
                fz="xl"
                fw={600}
                mb="xl"
                c="#1675e0"
              >
                {t('Savol va javoblar')}
              </Anchor>
            ) : null}

            {data?.news?.length > 0 && <News news={data?.news} />}
            {data.news?.[0]?.category_code ? (
              <Flex w="100%" justify="center" align="center" pt="md" className="print:!hidden">
                <DetailsLink
                  link={`/news/${data.news?.[0]?.category_code}?activity_id=${data.news?.[0]?.activity_id}`}
                  title={t('Barcha yangiliklar')}
                />
              </Flex>
            ) : null}
          </Content.Body>
        </Content>
      </Section>
    </Container>
  );
}
